import { api } from "../../../lib/api.js";

/**
 * Release artifacts client — the installer images a version ships.
 *
 * ONE TIER since 2026-09-16: `disk1` / `disk2`, for resellers, sales and
 * super admins (`releases:download_images` on this side). There was a third,
 * public kind — the release-notes document — and removing it is why this
 * module is short: the release notes are the CHANGE LOG, written on the
 * release and rendered on the page, so they are not a file anybody attaches,
 * uploads or downloads. Every artifact is a LINK to where the image is
 * hosted; this box never carries multi-gigabyte bytes.
 *
 * NOTHING HERE RETURNS A URL FROM A LIST. `listArtifacts` describes the files;
 * `requestDownload` is the only way to obtain a fetchable address, it is a
 * POST because it writes, and portal-api records the attempt whether it grants
 * or refuses. A convenience helper that cached the URL from a listing would
 * quietly re-open the unaudited path this design exists to close.
 *
 * Upstream: portal-api's `/api/v1/admin/releases/:id/artifacts*`, reached
 * through the dashboard's documentation proxy (dual-key). See
 * `apps/server/src/routes/documentation.ts`.
 */

/** The kinds, in the order the UI lists them. */
export const ARTIFACT_KINDS = ["disk1", "disk2"] as const;
export type ArtifactKind = (typeof ARTIFACT_KINDS)[number];

export const ARTIFACT_LABELS: Record<ArtifactKind, string> = {
  disk1: "Disk 1 Image",
  disk2: "Disk 2 Image",
};

/** Who can read each kind — rendered as the audience hint beside each row. */
export const ARTIFACT_AUDIENCE: Record<ArtifactKind, string> = {
  disk1: "Resellers, sales and super admins only",
  disk2: "Resellers, sales and super admins only",
};

export const RESTRICTED_KINDS: readonly ArtifactKind[] = ["disk1", "disk2"];

export function isRestrictedKind(kind: string): boolean {
  return (RESTRICTED_KINDS as readonly string[]).includes(kind);
}

export interface ReleaseArtifactDTO {
  id: number;
  kind: ArtifactKind;
  label: string;
  filename: string | null;
  byte_size: number | null;
  checksum: string | null;
  notes: string | null;
  active: boolean;
  restricted: boolean;
  external_url: string | null;
  download_count: number;
  denied_count: number;
  created_at: string;
  updated_at: string;
}

export interface ArtifactInput {
  kind: ArtifactKind;
  label?: string;
  external_url?: string;
  checksum?: string;
  byte_size?: number | null;
  notes?: string;
  active?: boolean;
}

/** What a granted download hands back. `url` is short-lived by design. */
export interface DownloadTicket {
  url: string;
  kind: ArtifactKind;
  filename: string | null;
  byte_size: number | null;
  checksum: string | null;
  expires_in: number;
}

const BASE = "/api/documentation/admin/releases";

export async function listArtifacts(releaseId: number): Promise<ReleaseArtifactDTO[]> {
  const res = await api<{ data?: ReleaseArtifactDTO[] }>(`${BASE}/${releaseId}/artifacts`);
  return res.data ?? [];
}

/** Create-or-replace. One artifact per kind per release is a DB invariant, so
 *  correcting a bad link does not require deleting the bad one first. */
export function saveArtifact(releaseId: number, input: ArtifactInput): Promise<unknown> {
  return api(`${BASE}/${releaseId}/artifacts`, {
    method: "POST",
    body: { artifact: input },
  });
}

export function updateArtifact(
  releaseId: number,
  kind: ArtifactKind,
  input: Partial<ArtifactInput>,
): Promise<unknown> {
  return api(`${BASE}/${releaseId}/artifacts/${kind}`, {
    method: "PATCH",
    body: { artifact: input },
  });
}

export function deleteArtifact(releaseId: number, kind: ArtifactKind): Promise<unknown> {
  return api(`${BASE}/${releaseId}/artifacts/${kind}`, { method: "DELETE" });
}


/**
 * Ask for a download. THE only way to get a fetchable address, and it is
 * recorded on both branches — a refusal lands in the download log exactly as a
 * grant does. Throws `ApiError` (403 / 404) when refused; the caller shows the
 * reason rather than inventing one.
 */
export async function requestDownload(
  releaseId: number,
  kind: ArtifactKind,
): Promise<DownloadTicket> {
  const res = await api<{ data: DownloadTicket }>(
    `${BASE}/${releaseId}/artifacts/${kind}/download`,
    { method: "POST" },
  );
  return res.data;
}

// ── The download log ────────────────────────────────────────────────────

export interface ReleaseDownloadDTO {
  id: number;
  release_id: number | null;
  release_version: string | null;
  artifact_kind: string;
  artifact_label: string;
  user_email: string | null;
  actor_kind: "staff" | "customer" | "reseller" | "unknown";
  organization_id: number | null;
  reseller_crm_id: number | null;
  reseller_name: string | null;
  outcome: "granted" | "denied";
  reason: string;
  ip_address: string | null;
  user_agent: string | null;
  created_at: string;
}

export interface DownloadLogFilters {
  page?: number;
  per_page?: number;
  outcome?: "granted" | "denied" | "";
  kind?: string;
  actor_kind?: string;
  release_id?: number | "";
  email?: string;
}

export interface DownloadLogPage {
  rows: ReleaseDownloadDTO[];
  totalCount: number;
  totalPages: number;
  grantedCount: number;
  deniedCount: number;
}

/** Why a download was refused, in words. The API sends machine codes so the
 *  specs can assert on them; the operator reading the log needs a sentence. */
export const DENIAL_REASONS: Record<string, string> = {
  ok: "Granted",
  not_permitted: "Not permitted for this account",
  artifact_inactive: "Artifact retired",
  artifact_missing: "No such artifact",
  no_source: "No file or link configured",
  unknown_actor: "Unauthenticated request",
};

/** The shared `api` helper takes no query object, so build the string here —
 *  one place, so the log's filters cannot drift from what the proxy reads. */
function downloadQuery(filters: DownloadLogFilters): string {
  const params = new URLSearchParams({
    page: String(filters.page ?? 1),
    per_page: String(filters.per_page ?? 25),
  });
  if (filters.outcome) params.set("outcome", filters.outcome);
  if (filters.kind) params.set("kind", filters.kind);
  if (filters.actor_kind) params.set("actor_kind", filters.actor_kind);
  if (filters.release_id) params.set("release_id", String(filters.release_id));
  if (filters.email) params.set("email", filters.email);
  return params.toString();
}

export async function listDownloads(filters: DownloadLogFilters = {}): Promise<DownloadLogPage> {
  const res = await api<{
    data?: ReleaseDownloadDTO[];
    meta?: {
      total_count?: number;
      total_pages?: number;
      granted_count?: number;
      denied_count?: number;
    };
  }>(`/api/documentation/admin/release-downloads?${downloadQuery(filters)}`);

  return {
    rows: res.data ?? [],
    totalCount: res.meta?.total_count ?? res.data?.length ?? 0,
    totalPages: res.meta?.total_pages ?? 1,
    grantedCount: res.meta?.granted_count ?? 0,
    deniedCount: res.meta?.denied_count ?? 0,
  };
}

/**
 * Hand the browser a granted download URL.
 *
 * NOT `window.location.assign`. The dashboard also runs inside the Tauri
 * desktop shell, where assigning `location` navigates the whole webview — so a
 * URL the shell does not hand off to the OS downloader replaces the ENTIRE app
 * with a blank page or a broken-document icon, and the operator's only way back
 * is to relaunch. Same family as the cross-origin PDF iframe that hung the
 * macOS shell (`docs/documentation.md` → "Inline PDF preview").
 *
 * A detached anchor with `target="_blank"` + `rel="noreferrer"` cannot take the
 * current document with it in either surface: the browser starts the transfer
 * (or opens a tab that immediately closes on a `Content-Disposition:
 * attachment`), and the webview hands the URL to the system browser.
 *
 * NOT `saveBlob` either: this is a cross-origin, short-lived URL to bytes
 * that never pass through this box (a multi-GB image on another server), so
 * there is no blob to hand the native Save-As dialog. That is why this module
 * is the ONE named exception to the CRM download wall
 * (`crm/crm-download-wall.test.ts`, which otherwise forbids a hand-rolled
 * `<a download>` inside the CRM tree): the wall protects blob saves in the
 * desktop webview, and this is a URL handoff the webview gives to the OS.
 */
export function startDownload(url: string, filename?: string | null): void {
  const anchor = document.createElement("a");
  anchor.href = url;
  anchor.target = "_blank";
  anchor.rel = "noreferrer";
  // A hint only: a cross-origin response's own Content-Disposition wins, which
  // is what actually names the file for both artifact sources.
  if (filename) anchor.download = filename;
  document.body.appendChild(anchor);
  anchor.click();
  anchor.remove();
}

/** Human byte size. Disk images are measured in GB, so this has to go that far. */
export function formatBytes(bytes: number | null | undefined): string {
  if (bytes === null || bytes === undefined || bytes <= 0) return "—";
  const units = ["B", "KB", "MB", "GB", "TB"];
  let value = bytes;
  let unit = 0;
  while (value >= 1024 && unit < units.length - 1) {
    value /= 1024;
    unit += 1;
  }
  return `${value >= 10 || unit === 0 ? Math.round(value) : value.toFixed(1)} ${units[unit]}`;
}
